'use client';
import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const REPORTS = [
  { code: 'RPT-001', name: 'Stock Position Report', lastGen: '29 Jul 2026 08:00', format: 'PDF+Excel' },
  { code: 'RPT-002', name: 'Inventory Movement Report', lastGen: '28 Jul 2026 18:00', format: 'PDF+Excel' },
  { code: 'RPT-003', name: 'Non-Variance Report', lastGen: '25 Jul 2026 09:00', format: 'PDF' },
  { code: 'RPT-006', name: 'GRN Register', lastGen: '29 Jul 2026 08:00', format: 'PDF+Excel' },
  { code: 'RPT-007', name: 'Issuance Register', lastGen: '29 Jul 2026 08:00', format: 'PDF+Excel' },
];

const AUDIT_TRAIL = [
  { user: 'Arjun Mehta', action: 'APPROVED GRN-2026-0845', time: '14 min ago', type: 'approve' },
  { user: 'Rahul Kumar', action: 'CREATED GRN-2026-0847 (Draft)', time: '2h ago', type: 'create' },
  { user: 'Priya Sharma', action: 'LOGGED QHSE Inspection — Site Beta', time: '3h ago', type: 'qhse' },
  { user: 'Arjun Mehta', action: 'ISSUED GI-2026-0317 to Kiran Bhat', time: '4h ago', type: 'issue' },
  { user: 'Meena Pillai', action: 'CREATED GI-2026-0319 (Pending)', time: '6h ago', type: 'create' },
  { user: 'System', action: 'LOW STOCK alert — Hydraulic Oil ISO 46', time: '6h ago', type: 'alert' },
];

const ACTION_COLOR: Record<string, string> = {
  approve: 'bg-teal/10 text-teal',
  create: 'bg-secondary/10 text-secondary',
  qhse: 'bg-purple-100 text-purple-600',
  issue: 'bg-orange-100 text-orange-600',
  alert: 'bg-amber-100 text-amber-600',
};

const ACTION_ICON: Record<string, string> = {
  approve: 'CheckCircleIcon',
  create: 'PlusCircleIcon',
  qhse: 'ShieldCheckIcon',
  issue: 'TruckIcon',
  alert: 'ExclamationTriangleIcon',
};

export default function AuditorDashboard() {
  return (
    <div className="space-y-5">
      {/* Role header */}
      <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-slate-600 to-slate-800 rounded-2xl text-white">
        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
          <Icon name="EyeIcon" size={22} />
        </div>
        <div>
          <p className="font-700 text-base">Full View — Read Only</p>
          <p className="text-[12px] text-white/65">Auditor · Full read access · No write permissions</p>
        </div>
        <div className="ml-auto">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-lg border border-white/20">
            <Icon name="LockClosedIcon" size={13} className="text-white/60" />
            <span className="text-[11px] text-white/70 font-500">View & Export Only</span>
          </div>
        </div>
      </div>

      {/* Read-only KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Inventory Value', value: '€ 847K', sub: 'Across 4 sites', icon: 'CubeIcon', color: 'bg-primary/5 border-primary/15', iconColor: 'bg-primary/10 text-primary' },
          { label: 'Open Transactions', value: '14', sub: '6 GRNs · 8 GIs', icon: 'ArrowsRightLeftIcon', color: 'bg-secondary/5 border-secondary/20', iconColor: 'bg-secondary/10 text-secondary' },
          { label: 'QHSE Issues', value: '7', sub: '3 expired · 4 expiring', icon: 'ShieldExclamationIcon', color: 'bg-amber-50 border-amber-200', iconColor: 'bg-amber-100 text-amber-600' },
          { label: 'Low Stock Items', value: '11', sub: '3 critical', icon: 'ExclamationTriangleIcon', color: 'bg-red-50 border-red-200', iconColor: 'bg-red-100 text-red-600' },
        ].map((k) => (
          <div key={k.label} className={`rounded-xl border p-4 shadow-card ${k.color}`}>
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${k.iconColor}`}>
              <Icon name={k.icon as Parameters<typeof Icon>[0]['name']} size={18} />
            </div>
            <p className="text-[10px] font-600 uppercase tracking-widest text-muted-foreground">{k.label}</p>
            <p className="text-3xl font-700 text-foreground tabular-nums mt-0.5">{k.value}</p>
            <p className="text-[11px] text-muted-foreground mt-1">{k.sub}</p>
          </div>
        ))}
      </div>

      {/* Reports + Audit trail */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-2xl shadow-card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Icon name="DocumentChartBarIcon" size={15} className="text-secondary" />
              <h3 className="text-sm font-600 text-foreground">Available Reports</h3>
            </div>
            <Link href="/reports" className="text-[11px] text-secondary hover:underline">All reports</Link>
          </div>
          <div className="divide-y divide-border">
            {REPORTS.map((r) => (
              <div key={r.code} className="flex items-center gap-3 px-5 py-3.5 hover:bg-muted/30 transition-colors group">
                <div className="w-8 h-8 rounded-lg bg-secondary/8 flex items-center justify-center shrink-0">
                  <Icon name="DocumentTextIcon" size={15} className="text-secondary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-700 text-muted-foreground font-mono-data">{r.code}</span>
                    <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded font-500">{r.format}</span>
                  </div>
                  <p className="text-[12px] font-600 text-foreground">{r.name}</p>
                  <p className="text-[10px] text-muted-foreground">Last: {r.lastGen}</p>
                </div>
                <button className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 px-2.5 py-1.5 bg-secondary text-white rounded-lg text-[11px] font-600 hover:bg-secondary/90">
                  <Icon name="ArrowDownTrayIcon" size={12} /> Export
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl shadow-card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Icon name="ClockIcon" size={15} className="text-muted-foreground" />
              <h3 className="text-sm font-600 text-foreground">Audit Trail</h3>
            </div>
            <Link href="/settings/audit-log" className="text-[11px] text-secondary hover:underline">Full log</Link>
          </div>
          <div className="divide-y divide-border">
            {AUDIT_TRAIL.map((a, i) => (
              <div key={`audit-${i}`} className="flex gap-3 px-5 py-3 hover:bg-muted/30 transition-colors">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${ACTION_COLOR[a.type]}`}>
                  <Icon name={ACTION_ICON[a.type] as Parameters<typeof Icon>[0]['name']} size={13} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-600 text-foreground">{a.user}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{a.action}</p>
                </div>
                <span className="text-[10px] text-muted-foreground/60 shrink-0 whitespace-nowrap">{a.time}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick navigation to all modules (read-only) */}
      <div className="bg-card border border-border rounded-2xl shadow-card p-5">
        <h3 className="text-sm font-600 text-foreground mb-4">Quick Access — All Modules</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Inventory', href: '/inventory/items', icon: 'CubeIcon', color: 'bg-secondary/8 text-secondary' },
            { label: 'Stock Positions', href: '/stock-positions', icon: 'ChartBarIcon', color: 'bg-teal/8 text-teal' },
            { label: 'GRN Register', href: '/grn-management', icon: 'DocumentTextIcon', color: 'bg-orange-50 text-orange-600' },
            { label: 'QHSE Register', href: '/qhse', icon: 'ShieldCheckIcon', color: 'bg-purple-50 text-purple-600' },
          ].map((m) => (
            <Link key={m.label} href={m.href} className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border border-border hover:border-secondary/30 hover:shadow-card transition-all ${m.color}`}>
              <Icon name={m.icon as Parameters<typeof Icon>[0]['name']} size={16} />
              <span className="text-[12px] font-600">{m.label}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
